import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from './lib/analytics';
import { Intro } from './routes/Intro';
import { QuestionPage } from './routes/QuestionPage';
import { LeadCapture } from './routes/LeadCapture';
import { BuildingReport } from './routes/BuildingReport';
import { RetakeBuilding } from './routes/RetakeBuilding';
import { Pricing } from './routes/Pricing';
import { Checkout } from './routes/Checkout';
import { ThankYou } from './routes/ThankYou';
import { Login } from './routes/Login';
import { Booking } from './routes/Booking';
import { RefundPolicy } from './routes/RefundPolicy';

// Dashboard lives in a separate chunk — only loaded for authenticated users.
const DashboardRoutes = lazy(() => import('./dashboard/DashboardRoutes'));
// Persona evaluation harness — also lazy so the persona JSON outputs don't
// bloat the main bundle.
const PersonasIndex = lazy(() => import('./routes/PersonasIndex').then((m) => ({ default: m.PersonasIndex })));
const PersonaView = lazy(() => import('./routes/PersonaView').then((m) => ({ default: m.PersonaView })));
const AdminFunnel = lazy(() => import('./routes/AdminFunnel').then((m) => ({ default: m.AdminFunnel })));
const Support = lazy(() => import('./routes/Support').then((m) => ({ default: m.Support })));
const CoachView = lazy(() => import('./routes/CoachView').then((m) => ({ default: m.CoachView })));
// AI Interview Coach add-on — own chunk (pdf/voice deps stay out of the main bundle).
const IcApp = lazy(() => import('./ic/IcApp').then((m) => ({ default: m.IcApp })));
const StubPreview = lazy(() => import('./routes/StubPreview').then((m) => ({ default: m.StubPreview })));

/**
 * Reset scroll position to the top whenever the route changes. React Router
 * doesn't do this automatically — without this, navigating from a long
 * scrolled page (e.g. /pricing) to a new page (/checkout/:plan) leaves the
 * browser at whatever scroll position the previous page was at.
 * Skipped when the URL contains a hash (#section) so anchor links still work.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

/**
 * Push a `pageView` to the GTM dataLayer on every route change — once per
 * distinct pathname (hash/query changes don't re-fire, so dashboard anchor
 * links and ?variant= swaps don't double-count). Fires on the initial load
 * too (the effect runs on mount). Keyed on pathname only so a single SPA
 * navigation = a single pageView, matching the jobtest.org GTM spec.
 */
function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView();
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <PageViewTracker />
      <Routes>
      <Route path="/" element={<Intro />} />
      <Route path="/q/:questionId" element={<QuestionPage />} />
      <Route path="/lead" element={<LeadCapture />} />
      <Route path="/building-report" element={<BuildingReport />} />
      <Route path="/retake/building" element={<RetakeBuilding />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/checkout/:plan" element={<Checkout />} />
      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/login" element={<Login />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route
        path="/dashboard/*"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <DashboardRoutes />
          </Suspense>
        }
      />
      <Route
        path="/personas"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <PersonasIndex />
          </Suspense>
        }
      />
      <Route
        path="/personas/:id"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <PersonaView />
          </Suspense>
        }
      />
      <Route
        path="/admin"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <AdminFunnel />
          </Suspense>
        }
      />
      <Route
        path="/support"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <Support />
          </Suspense>
        }
      />
      <Route
        path="/coach-view"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <CoachView />
          </Suspense>
        }
      />
      <Route
        path="/interview-coach/*"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <IcApp />
          </Suspense>
        }
      />
      <Route
        path="/dev/stub-preview"
        element={
          <Suspense fallback={<DashboardLoading />}>
            <StubPreview />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function DashboardLoading() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-canvas">
      <p className="text-ink-soft text-sm">Loading…</p>
    </div>
  );
}
